import React, { useLayoutEffect, useRef } from 'react';
import { AnimationConfigWithPath } from 'lottie-web';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { ScrollMagic } from '@vendor/scrollmagic';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';

interface AnimationConfigWithForcePath extends Omit<AnimationConfigWithPath, 'container'> {
  path: string;
}

const lottieConfig: AnimationConfigWithForcePath = {
  path: cloudAssetUrl('wedding-invitations/unified-rsvp/bodymovin/data.json'), // Required
  renderer: 'svg', // Required
  loop: false, // Optional
  autoplay: false, // Optional
  name: 'announcements streaming',
  rendererSettings: {
    progressiveLoad: false,
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export const AllTogetherSectionMobileController = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const partiesRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const partyConfig: AnimationConfigWithForcePath = { ...lottieConfig, initialSegment: [252, 255] };
  const { animationRef: tableAnimationRef, loadAnimation: loadTable } = useLottieAnimation(lottieConfig, {
    containerRef: tableRef,
    speed: 1.5,
  });
  const { animationRef: partiesAnimationRef, loadAnimation: loadParties } = useLottieAnimation(partyConfig, {
    containerRef: partiesRef,
    speed: 1.5,
  });
  const initBodyMovinAnimation = () => {
    loadTable();
    loadParties();
  };

  useLayoutEffect(() => {
    let tableScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let partyScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    if (scrollController && isMounted) {
      const startTableAnimation = () => {
        tableScene = new ScrollMagic.Scene({
          triggerElement: '#together-table-lottie',
          triggerHook: 0.5,
          offset: 200,
          duration: 700,
        })
          .addTo(scrollController)
          .on('enter', () => {
            const firstLottieEndFrames = 89;
            tableAnimationRef.current?.playSegments([0, firstLottieEndFrames], true);
          });
      };
      const startPartyAnimation = () => {
        partyScene = new ScrollMagic.Scene({
          triggerElement: '#household-party-lottie',
          triggerHook: 0,
          duration: 300,
        })
          .addTo(scrollController)
          .on('enter', () => {
            const secondLottieStartFrames = 245;
            const secondLottieEndFrames = 310;
            partiesAnimationRef.current?.playSegments([secondLottieStartFrames, secondLottieEndFrames], true);
          });
      };
      startTableAnimation();
      startPartyAnimation();
    }
    const clearScene = () => {
      if (tableScene) {
        tableScene.destroy(true);
      }
      if (partyScene) {
        partyScene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, tableAnimationRef, partiesAnimationRef]);

  return {
    tableRef,
    partiesRef,
    initBodyMovinAnimation,
  };
};
